'use client';

import { Collapse } from '@/uiComposites/interactive/Collapse';
import {
	useAccordionItemOpened,
	useDispatchAccordion,
} from '@/uiComposites/interactive/accordion/AccordionProvider.client';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { ReactNode, useId } from 'react';
import {
	stationShelf,
	stationOccupy,
	stationClosed,
	station,
	stationOpened,
} from './StepByStep.css';
import { A11YAccordionActionLabel } from '@/uiComposites/interactive/accordion/A11YAccordionActionLabel';

export const StepByStepAccordionItem = ({
	summarySlot,
	children,
}: {
	summarySlot: ReactNode;
	children: ReactNode;
}) => {
	const id = useId();
	const isOpened = useAccordionItemOpened(id);
	const dispatchAccordion = useDispatchAccordion();

	const htmlId = useId();

	return (
		<div key={id}>
			<button
				aria-controls={htmlId}
				aria-expanded={isOpened}
				onClick={() => {
					dispatchAccordion({
						type: 'toggle',
						id,
					});
				}}
				className={classes(
					stationShelf,
					box({
						inlineSize: '100%',
					}),
					textStyle({
						color: 'positiveBlue',
						fontWeight: 'bold',
					}),
				)}
				{...addTestSelector(`uiAccordionItem`)}
			>
				<div className={classes(stationOccupy)}>
					<div
						className={classes(
							station['minor'],
							isOpened ? stationOpened : stationClosed,
						)}
					>
						<span
							aria-hidden="true"
							className={classes(
								textBlock({
									fontWeight: 'bold',
									capSize: '1 | h3',
								}),
							)}
						>
							{isOpened ? '–' : '+'}
						</span>
					</div>
				</div>
				<A11YAccordionActionLabel isOpened={isOpened} />
				<h3
					className={classes(
						textBlock({
							capLineHeight: 'tight',
							color: isOpened ? 'positive' : undefined,
						}),
					)}
				>
					{summarySlot}
				</h3>
			</button>
			<Collapse id={htmlId} isOpened={isOpened}>
				<div
					className={classes(
						box({
							paddingBlockStart: '5 | betweenParagraphs',
							paddingBlockEnd: 'negative 0 | p',
						}),
					)}
					{...addTestSelector(`uiAccordionContent`)}
				>
					{children}
				</div>
			</Collapse>
		</div>
	);
};
